// src/services/cache/serverCache.js
// Client-side reader for server-written cache snapshots.
//
// NEVER calls upstream APIs directly.
// Reads from Firestore public_snapshots/{key} only.
// Falls back to IndexedDB (APICache) if Firestore is unavailable.
// 200,000 users reading the same Firestore doc = 200,000 Firestore reads,
// NOT 200,000 upstream API calls.

import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { APICache } from '@services/storage/idb';

const SNAPSHOT_COLLECTION = 'public_snapshots';

// ── One-shot read ─────────────────────────────────────────────────────────
export async function readSnapshot(key) {
  try {
    // Guard: ensure Firebase is initialized before calling getFirestore
    const { getApps } = await import('firebase/app');
    if (!getApps().length) {
      // Firebase not yet initialized — fall through to IDB fallback
      throw new Error('Firebase not initialized');
    }
    const db = getFirestore();
    const ref = doc(db, SNAPSHOT_COLLECTION, key);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;

    const d = snap.data();

    // Check expiry
    if (d.expiresAt && new Date(d.expiresAt) < new Date()) {
      d.stale = true;
    }

    // Mirror to local IDB for offline fallback
    await APICache.set(`snapshot-${key}`, d, 10 * 60 * 1000).catch(() => {});

    return d;
  } catch (err) {
    // Firestore unavailable — try IDB fallback
    console.warn(`[serverCache] Firestore unavailable for ${key}, trying IDB:`, err.message);
    const idbFallback = await APICache.get(`snapshot-${key}`).catch(() => null);
    if (idbFallback) {
      idbFallback.stale = true;
      idbFallback.fromIDBFallback = true;
      return idbFallback;
    }
    return null;
  }
}

// ── Real-time subscription ────────────────────────────────────────────────
// Use for live operational dashboard panels.
// One Firestore listener per key — shared across all components via React context.
export function subscribeSnapshot(key, onData, onError) {
  const db = getFirestore();
  const ref = doc(db, SNAPSHOT_COLLECTION, key);

  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) { onData(null); return; }
    const d = snap.data();
    if (d.expiresAt && new Date(d.expiresAt) < new Date()) d.stale = true;
    onData(d);
  }, onError);
}

// ── Convenience readers ───────────────────────────────────────────────────
export const readNWSAlerts       = () => readSnapshot('nws_alerts');
export const readUSGSGauges      = () => readSnapshot('usgs_gauges');
export const readNOAASurge       = () => readSnapshot('noaa_surge');
export const readDOTDTraffic     = () => readSnapshot('dotd_traffic');
export const readEntergyOutages  = () => readSnapshot('entergy_outages');
export const readShelterStatus   = () => readSnapshot('shelter_status');
export const readTomorrowCurrent = () => readSnapshot('tomorrow_current');

// ── Staleness helpers ─────────────────────────────────────────────────────
// RENDERING RULE: stale/null snapshots must never be shown as current.
export function isSnapshotFresh(snapshot) {
  if (!snapshot) return false;
  if (snapshot.stale) return false;
  if (!snapshot.expiresAt) return false;
  return new Date(snapshot.expiresAt) > new Date();
}

export function snapshotAgeMs(snapshot) {
  if (!snapshot?.fetchedAt) return null;
  return Date.now() - new Date(snapshot.fetchedAt).getTime();
}
